import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy,
  Download,
  Import,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { fmtLocalDate, fmtLocalDateTime } from '@/lib/dateTimeLocal';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { TransmitterEditModal } from '@/features/operations-transmitters/components/TransmitterEditModal';
import { EditSensorDialog } from '@/features/operations-sensors/components/EditSensorDialog';
import { AccountsColumnsMenu } from './AccountsColumnsMenu';
import { AccountsColumnResizeHandle } from './AccountsColumnResizeHandle';
import { useColumnState } from '@/hooks/useColumnState';
import { getAccountTransmitters } from '../api';
import ReassignDialog from './ReassignDialog';
import ImportTransmittersModal from './ImportTransmittersModal';
import type { TransmitterDetail, AccountDashboardData } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers: TransmitterRowHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

interface TransmitterRowHelpers {
  onEdit: (transmitter: TransmitterDetail) => void;
  onReassign: (transmitter: TransmitterDetail) => void;
  onSensorClick: (sensorMongoId: string) => void;
}

const STATUS_OPTIONS = [
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' },
];

const OPERATIONAL_STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Not Activated', value: 'not_activated' },
];

const OPERATIONAL_WINDOW_MS = 2 * 60 * 60 * 1000;

function parseSelectedFilterValue(value?: string): string[] {
  try {
    return value ? JSON.parse(value) : [];
  } catch {
    return [];
  }
}

function getOperationalStatus(transmitter: TransmitterDetail): 'active' | 'inactive' | 'not_activated' {
  if (!transmitter.activated) {
    return 'not_activated';
  }

  if (!transmitter.lastpacketAt) {
    return 'inactive';
  }

  const lastPacketAt = new Date(transmitter.lastpacketAt).getTime();
  if (Number.isNaN(lastPacketAt)) {
    return 'inactive';
  }

  return lastPacketAt > Date.now() - OPERATIONAL_WINDOW_MS ? 'active' : 'inactive';
}

const COLUMNS: ColumnDef<TransmitterDetail>[] = [
  {
    key: 'devId',
    label: 'Dev ID',
    sortKey: 'devId',
    filterType: 'text',
    getValue: (t) => t.devId,
    render: (t, { onEdit }) => (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(t);
        }}
        className="font-medium text-atek-blue hover:underline text-left"
        title="Open transmitter detail"
      >
        {t.devId}
      </button>
    ),
  },
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (t) => t.name || '',
    render: (t, { onEdit }) => {
      if (!t.name) return <span className="text-muted-foreground">—</span>;
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(t);
          }}
          className="max-w-[220px] truncate block text-atek-blue hover:underline text-left"
          title={`Open transmitter detail: ${t.name}`}
        >
          {t.name}
        </button>
      );
    },
  },
  {
    key: 'deviceType',
    label: 'Device Type',
    filterType: 'text',
    getValue: (t) => t.deviceType || '',
  },
  {
    key: 'sensorCount',
    label: 'Sensors',
    // Each sensor name is a clickable link to the sensor detail modal.
    // Falls back to the count when the sensors array is missing (legacy
    // payloads or aggregation errors).
    getValue: (t) => t.sensors?.map((s) => s.name).join(', ') ?? String(t.sensorCount),
    noFilter: true,
    render: (t, { onSensorClick }) => {
      const list = t.sensors || [];
      if (list.length === 0) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <div className="flex flex-wrap gap-x-1.5 gap-y-0.5 max-w-[260px]">
          {list.map((s, i) => (
            <span key={s.mongoId} className="text-sm">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onSensorClick(s.mongoId);
                }}
                className="text-atek-blue hover:underline truncate"
                title={`Open sensor detail: ${s.name}`}
              >
                {s.name}
              </button>
              {i < list.length - 1 ? ',' : ''}
            </span>
          ))}
        </div>
      );
    },
  },
  {
    key: 'siteName',
    label: 'Site',
    filterType: 'text',
    getValue: (t) => t.siteName || '',
  },
  {
    key: 'locationName',
    label: 'Location',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.locationName || '',
  },
  {
    key: 'enabledState',
    label: 'Enabled',
    filterType: 'select',
    filterOptions: STATUS_OPTIONS,
    getValue: (t) => (t.activeFlag ? 'enabled' : 'disabled'),
    render: (t) => (
      <Badge variant={t.activeFlag ? 'success' : 'secondary'}>
        {t.activeFlag ? 'Enabled' : 'Disabled'}
      </Badge>
    ),
  },
  {
    key: 'operationalStatus',
    label: 'Operational',
    filterType: 'select',
    filterOptions: OPERATIONAL_STATUS_OPTIONS,
    getValue: (t) => getOperationalStatus(t),
    render: (t) => {
      const status = getOperationalStatus(t);
      if (status === 'active') {
        return <Badge variant="success">Active</Badge>;
      }
      if (status === 'inactive') {
        return <Badge variant="danger">Inactive</Badge>;
      }
      return <Badge variant="secondary">Not Activated</Badge>;
    },
  },
  {
    key: 'lastpacketAt',
    label: 'Last Packet',
    sortKey: 'lastpacketAt',
    filterType: 'date-range',
    getValue: (t) => t.lastpacketAt || '',
    render: (t) => (
      <span className="tabular-nums text-sm">
        {t.lastpacketAt ? fmtLocalDateTime(t.lastpacketAt) : '—'}
      </span>
    ),
  },
  {
    key: 'rssi',
    label: 'RSSI',
    sortKey: 'rssi',
    filterType: 'number-range',
    defaultVisible: false,
    getValue: (t) => t.rssi ?? null,
    render: (t) => {
      if (t.rssi == null) return '—';
      const color =
        t.rssi > -70
          ? 'text-green-600'
          : t.rssi > -90
            ? 'text-yellow-600'
            : 'text-red-600';
      return <span className={color}>{t.rssi}</span>;
    },
    align: 'right',
  },
  {
    key: 'batteryPercentage',
    label: 'Battery',
    sortKey: 'batteryPercentage',
    filterType: 'number-range',
    defaultVisible: false,
    getValue: (t) => (t.batteryPercentage ? parseFloat(t.batteryPercentage) : null),
    render: (t) => {
      if (!t.batteryPercentage) return '—';
      const val = parseFloat(t.batteryPercentage);
      const color =
        val > 50
          ? 'text-green-600'
          : val > 20
            ? 'text-yellow-600'
            : 'text-red-600';
      return <span className={color}>{val}%</span>;
    },
    align: 'right',
  },
  {
    key: 'currentFirmwareName',
    label: 'Firmware',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.currentFirmwareName || '',
  },
  {
    key: 'simprovider',
    label: 'SIM Provider',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.simprovider || '',
  },
  {
    key: 'iccid',
    label: 'ICCID',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.iccid || '',
  },
  {
    key: 'xbeeSl',
    label: 'XBee SL',
    filterType: 'text',
    defaultVisible: false,
    getValue: (t) => t.xbeeSl || '',
  },
  {
    key: 'installedAt',
    label: 'Installed',
    sortKey: 'installedAt',
    filterType: 'date-range',
    defaultVisible: false,
    getValue: (t) => t.installedAt || '',
    render: (t) => (t.installedAt ? fmtLocalDate(t.installedAt) : '—'),
  },
  {
    key: 'createdAt',
    label: 'Created',
    sortKey: 'createdAt',
    filterType: 'date-range',
    getValue: (t) => t.createdAt,
    render: (t) => (
      <span className="tabular-nums text-sm">{fmtLocalDate(t.createdAt)}</span>
    ),
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (t, { onEdit, onReassign }) => (
      <div className="flex items-center gap-2">
        {t.mongoTransmitterId && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(t);
            }}
            className="text-atek-blue hover:underline text-sm cursor-pointer"
          >
            Edit
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onReassign(t);
          }}
          className="text-muted-foreground hover:text-foreground cursor-pointer"
          title="Reassign to another account"
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
      </div>
    ),
    align: 'right',
  },
];

// =============================================================================
// localStorage Persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-account-transmitters-column-visibility';
const FILTERS_STORAGE_KEY_PREFIX = 'atek-account-transmitters-column-filters';
const ORDER_STORAGE_KEY = 'atek-account-transmitters-column-order';
const WIDTHS_STORAGE_KEY = 'atek-account-transmitters-column-widths';
const DEFAULT_COLUMN_WIDTH = 160;

// Column visibility/order/widths persistence is delegated to the generic
// `useColumnState` hook — see src/hooks/useColumnState.ts.

function loadColumnFilters(accountId: string): Record<string, string> {
  try {
    const stored = localStorage.getItem(`${FILTERS_STORAGE_KEY_PREFIX}-${accountId}`);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return {};
}

// =============================================================================
// CSV Export Utilities
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: TransmitterDetail[], columns: ColumnDef<TransmitterDetail>[], filename: string) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: TransmitterDetail[], columns: ColumnDef<TransmitterDetail>[]): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// =============================================================================
// SortableHeader Component
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter(
  col: ColumnDef<TransmitterDetail>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// TransmittersTab Component (Mode B: server-side pagination + client-side refinement)
// =============================================================================

interface TransmittersTabProps {
  accountId: string;
}

// One-shot fetch — server cap is 5000 (see routes/v4/accounts.js).
// No account has anywhere near that many transmitters; same pattern as
// SensorsTab and the main accounts list. Lets us drop pagination.
const PAGE_SIZE = 5000;

export default function TransmittersTab({ accountId }: TransmittersTabProps) {
  const queryClient = useQueryClient();

  // Server-side state
  const [page, setPage] = useState(0);

  // Client-side column filters (refinement within page)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => loadColumnFilters(accountId));
  // Sensor detail modal — opened by clicking a sensor name inside a transmitter row.
  const [editSensorId, setEditSensorId] = useState<string | null>(null);

  // Visibility/order/widths managed by the shared hook (src/hooks/useColumnState.ts).
  const {
    orderedColumns,
    columnWidths,
    isVisible,
    toggleColumn,
    setColumnOrder,
    setColumnWidth,
  } = useColumnState<ColumnDef<TransmitterDetail>>({
    columns: COLUMNS,
    storageKeys: {
      visibility: VISIBILITY_STORAGE_KEY,
      order: ORDER_STORAGE_KEY,
      widths: WIDTHS_STORAGE_KEY,
    },
  });

  const widthFor = (key: string) => columnWidths[key] ?? DEFAULT_COLUMN_WIDTH;

  // Client-side sort (within page)
  const [sortKey, setSortKey] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const [isExportingAll, setIsExportingAll] = useState(false);

  // Modal state
  const [editingTransmitterId, setEditingTransmitterId] = useState<string | null>(null);
  const [reassignTransmitter, setReassignTransmitter] = useState<TransmitterDetail | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const dashboardData = queryClient.getQueryData<AccountDashboardData>(['account-dashboard', accountId]);
  const accountName = dashboardData?.account?.name || 'Current Account';
  const hasLegacyOrg = !!(dashboardData?.account?.mongo_organisation_id || dashboardData?.account?.organisation_id);

  // Persist column filters to localStorage
  useEffect(() => {
    localStorage.setItem(`${FILTERS_STORAGE_KEY_PREFIX}-${accountId}`, JSON.stringify(columnFilters));
  }, [columnFilters, accountId]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // One-shot URL hydration — see equivalent block in SensorsTab.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const status = searchParams.get('status');
    const battery = searchParams.get('battery');
    if (!status && !battery) return;
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (status) next.operationalStatus = JSON.stringify([status]);
      if (battery === 'low') next.batteryPercentage = '>>20';
      return next;
    });
    const sp = new URLSearchParams(searchParams);
    sp.delete('status');
    sp.delete('battery');
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabledStatesFilter = useMemo(
    () => parseSelectedFilterValue(columnFilters.enabledState),
    [columnFilters.enabledState],
  );

  const operationalStatusesFilter = useMemo(
    () => parseSelectedFilterValue(columnFilters.operationalStatus),
    [columnFilters.operationalStatus],
  );

  const enabledStatesParam = useMemo(
    () => (enabledStatesFilter.length > 0 ? enabledStatesFilter.join(',') : undefined),
    [enabledStatesFilter],
  );

  const operationalStatusesParam = useMemo(
    () => (operationalStatusesFilter.length > 0 ? operationalStatusesFilter.join(',') : undefined),
    [operationalStatusesFilter],
  );

  // Extract text column filter values for server-side search
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  useEffect(() => {
    setPage(0);
  }, [enabledStatesParam, operationalStatusesParam]);

  const visibleColumns = orderedColumns.filter((col) => isVisible(col.key));

  // Server-side data fetch
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'account-transmitters',
      accountId,
      page,
      debouncedColumnSearch,
      enabledStatesParam,
      operationalStatusesParam,
    ],
    queryFn: () =>
      getAccountTransmitters(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        search: debouncedColumnSearch || undefined,
        enabledStates: enabledStatesParam,
        operationalStatuses: operationalStatusesParam,
      }),
  });

  const transmitters = useMemo(() => data?.data || [], [data?.data]);
  const total = data?.total || 0;

  // Client-side column filtering (AND-combined, within current page)
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return transmitters;

    return transmitters.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [transmitters, columnFilters]);

  // Client-side sorting (within filtered page)
  const sortedItems = useMemo(() => {
    if (!sortKey) return filteredItems;
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortDirection;
      }
      return String(aVal).localeCompare(String(bVal)) * sortDirection;
    });
  }, [filteredItems, sortKey, sortDirection]);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  const handleExportAllTransmitters = async () => {
    try {
      setIsExportingAll(true);
      const allItems: TransmitterDetail[] = [];
      const batchSize = 100;
      let pageNum = 0;
      let totalCount = 0;

      do {
        const batch = await getAccountTransmitters(accountId, {
          pageNumber: pageNum,
          pageLength: batchSize,
          search: debouncedColumnSearch || undefined,
          enabledStates: enabledStatesParam,
          operationalStatuses: operationalStatusesParam,
        });
        allItems.push(...(batch.data || []));
        totalCount = batch.total ?? 0;
        pageNum++;
      } while (allItems.length < totalCount);

      exportToCSV(allItems, COLUMNS, 'account-transmitters-all');
      toast.success(`Exported ${allItems.length} transmitters`);
    } catch (e) {
      console.error('Export all transmitters failed:', e);
      toast.error('Failed to export all transmitters');
    } finally {
      setIsExportingAll(false);
    }
  };

  // Row helpers for column renderers
  const rowHelpers: TransmitterRowHelpers = {
    onEdit: (t) => setEditingTransmitterId(t.mongoTransmitterId),
    onReassign: setReassignTransmitter,
    onSensorClick: (mongoSensorId: string) => setEditSensorId(mongoSensorId),
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading transmitters</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Transmitters</CardTitle>
            <CardDescription>
              {filteredItems.length < transmitters.length
                ? `Showing ${filteredItems.length} of ${transmitters.length} on this page`
                : `Showing ${transmitters.length} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyToClipboard(sortedItems, COLUMNS);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExportingAll}>
                  <Download className="h-4 w-4 mr-1" />
                  {isExportingAll ? 'Exporting...' : 'CSV'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export Transmitters</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    exportToCSV(sortedItems, COLUMNS, 'account-transmitters');
                    toast.success(`Exported ${sortedItems.length} rows`);
                  }}
                >
                  Export Page ({sortedItems.length} rows)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllTransmitters}>
                  Export All Transmitters ({total})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Columns menu — drag-to-reorder + visibility, matches accounts list. */}
            <AccountsColumnsMenu
              orderedColumns={orderedColumns}
              isVisible={isVisible}
              onToggle={toggleColumn}
              onReorder={setColumnOrder}
            />
            {hasLegacyOrg && (
              <Button
                variant="default"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setIsImportOpen(true)}
              >
                <Import className="h-4 w-4 mr-1" />
                Import Transmitters
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table
            // table-layout: fixed + explicit colgroup so resized widths apply
            // uniformly across filter row, header row, and body rows.
            style={{
              tableLayout: 'fixed',
              width: `${visibleColumns.reduce((sum, c) => sum + widthFor(c.key), 0)}px`,
            }}
          >
            <colgroup>
              {visibleColumns.map((col) => (
                <col key={col.key} style={{ width: `${widthFor(col.key)}px` }} />
              ))}
            </colgroup>
            <TableHeader>
              {/* ROW 1: Column filters (always visible) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
              </TableRow>

              {/* ROW 2: Column labels + sort + resize handles */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn('relative', col.align === 'right' && 'text-right')}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                    {!col.noFilter && (
                      <AccountsColumnResizeHandle
                        columnKey={col.key}
                        currentWidth={widthFor(col.key)}
                        onResize={setColumnWidth}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty state */}
              {!isLoading && sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No transmitters found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && sortedItems.map((transmitter) => (
                <TableRow
                  key={transmitter.id}
                  className="hover:bg-muted/50"
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        'text-sm',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(transmitter, rowHelpers)
                        : (String(col.getValue(transmitter) ?? '') || '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      </CardContent>

      {/* Transmitter Edit Modal */}
      <TransmitterEditModal
        transmitterId={editingTransmitterId}
        open={!!editingTransmitterId}
        onOpenChange={(open) => {
          if (!open) setEditingTransmitterId(null);
        }}
        organisationId={dashboardData?.account?.mongo_organisation_id}
      />

      {/* Sensor Edit Modal — opened by clicking a sensor name inside the
          Sensors cell. Same modal the per-account Sensors tab uses. */}
      <EditSensorDialog
        sensorId={editSensorId}
        open={!!editSensorId}
        onOpenChange={(open) => {
          if (!open) setEditSensorId(null);
        }}
      />

      {/* Reassign Dialog */}
      <ReassignDialog
        entityType="transmitter"
        entityId={reassignTransmitter?.id || ''}
        entityLabel={reassignTransmitter?.name || reassignTransmitter?.devId || ''}
        currentAccountId={accountId}
        currentAccountName={accountName}
        open={!!reassignTransmitter}
        onOpenChange={(open) => { if (!open) setReassignTransmitter(null); }}
      />

      <ImportTransmittersModal
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        accountId={accountId}
        accountName={accountName}
      />
    </Card>
  );
}
